import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ESTADOS = ["Nuevo", "Contactado", "Afiliado", "Descartado"];
const ESTADO_COLORS = {
  "Nuevo": "bg-[#C4674F]/15 text-[#A0553F]",
  "Contactado": "bg-[#F1F3F3] text-[#3E5641]",
  "Afiliado": "bg-[#A0553F] text-white",
  "Descartado": "bg-red-50 text-red-700",
};

export default function AffiliateApplicationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/affiliate-applications");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((it) => {
    if (estadoFilter !== "all" && it.estado !== estadoFilter) return false;
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.nombre, it.correo, it.telefono, it.ciudad].filter(Boolean).some((v) => v.toLowerCase().includes(t));
  }), [items, q, estadoFilter]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar esta solicitud de afiliado?")) return;
    try { await api.delete(`/affiliate-applications/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="admin-affiliate-applications">
      <header className="mb-8">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo 06</p>
        <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">Solicitudes de afiliados</h1>
        <p className="mt-1 text-sm text-[#6B7678]">
          Personas individuales que quieren vender autos y servicios de La Dupla Automotriz por comisión.
          Seguimiento manual por WhatsApp — no hay automatización.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input placeholder="Buscar por nombre, correo, ciudad…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="af-search" />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56" data-testid="af-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <AsyncListShell
        loading={loading}
        isEmpty={filtered.length === 0}
        loadingTestId="af-loading"
        emptyTestId="af-empty"
        emptyLabel="Sin solicitudes de afiliados aún."
      >
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Contacto</th>
                  <th className="px-4 py-3">Ciudad</th>
                  <th className="px-4 py-3">Experiencia</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((it) => (
                  <tr key={it.id} className="hover:bg-[#F1F3F3]/50" data-testid={`af-row-${it.id}`}>
                    <td className="px-4 py-3 font-medium text-[#3E5641]">{it.nombre}</td>
                    <td className="px-4 py-3">
                      <div>{it.telefono}</div>
                      <div className="text-xs text-[#6B7678]">{it.correo}</div>
                    </td>
                    <td className="px-4 py-3">{it.ciudad}</td>
                    <td className="px-4 py-3 text-xs">{it.experiencia_ventas ? "Sí" : "No"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-sm px-2 py-1 text-xs ${ESTADO_COLORS[it.estado] || ""}`}>{it.estado}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(it)} className="mr-2 rounded-sm p-1 text-[#3E5641] hover:bg-[#F1F3F3]" data-testid={`af-edit-${it.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(it.id)} className="rounded-sm p-1 text-red-600 hover:bg-red-50" data-testid={`af-delete-${it.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && <AffiliateDialog key={editing.id} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function AffiliateDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre: item.nombre, telefono: item.telefono, correo: item.correo, ciudad: item.ciudad,
    experiencia_ventas: item.experiencia_ventas ? "si" : "no",
    estado: item.estado, notas: item.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/affiliate-applications/${item.id}`, {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        correo: form.correo.trim(),
        ciudad: form.ciudad.trim(),
        experiencia_ventas: form.experiencia_ventas === "si",
        estado: form.estado,
        notas: form.notas?.trim() || null,
      });
      toast.success("Solicitud actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{item.nombre}</DialogTitle></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <F label="Nombre"><Input value={form.nombre} onChange={(e) => setF("nombre", e.target.value)} /></F>
          <F label="Teléfono"><Input value={form.telefono} onChange={(e) => setF("telefono", e.target.value)} /></F>
          <F label="Correo"><Input type="email" value={form.correo} onChange={(e) => setF("correo", e.target.value)} /></F>
          <F label="Ciudad"><Input value={form.ciudad} onChange={(e) => setF("ciudad", e.target.value)} /></F>
          <div>
            <Label className="field-label">¿Experiencia en ventas?</Label>
            <Select value={form.experiencia_ventas} onValueChange={(v) => setF("experiencia_ventas", v)}>
              <SelectTrigger data-testid="af-exp"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="si">Sí</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="field-label">Estado</Label>
            <Select value={form.estado} onValueChange={(v) => setF("estado", v)}>
              <SelectTrigger data-testid="af-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="field-label">Notas internas</Label>
          <Textarea rows={3} value={form.notas} onChange={(e) => setF("notas", e.target.value)} data-testid="af-notas" />
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="af-save">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) { return <div><Label className="field-label">{label}</Label>{children}</div>; }
